import { useState, useEffect } from "react";
import { Custom } from "components";
import { useApp } from "context";
import { useForm } from "hooks";
import { IUser } from "interfaces";
import { useNavigate } from "react-router-dom";
import { Enums, Helpers, Routes, Store } from "utils";

type Props = {
  id?: string;
};

export const SignIn = (props: Props) => {
  const { setFeedback, setUser, service, t } = useApp();

  const navigate = useNavigate();

  const [expiry, setExpiry] = useState<number>(Infinity);

  useEffect(() => {
    if (props.id) {
      service.auth.findCodeExpiry(props.id).then(([status, response]) => {
        setExpiry(response ?? 0);
      });
    }
  }, []);

  useEffect(() => {
    if (props.id && expiry < Math.floor(Date.now() / 1000)) {
      navigate(Routes.auth.signin);
    }
  });

  const initialState = props.id ? { email: "" } : { email: "", password: "" };

  const { onChange, onSubmit, values } = useForm(submitCallback, initialState);

  async function submitCallback() {
    const { email, password }: any = values;

    if (Helpers.hasValue(email) && (props.id || Helpers.hasValue(password))) {
      const [status, response] = props.id
        ? await service.auth.signin(email, { uuid: props.id })
        : await service.auth.requestCode(
            email,
            { password },
            Enums.EnumAuthMethod.Phone
          );

      if (status === Enums.EnumResponse.Success) {
        setFeedback(null);
        setUser({ email, ...response });
        if (props.id) {
          navigate(Routes.patient.home);
        } else {
          navigate(
            Helpers.interpolateURL(Routes.auth.accountVerification, {
              method: response.phone
                ? Enums.EnumAuthMethod.Phone
                : Enums.EnumAuthMethod.Email,
            })
          );
        }
      } else if (status === Enums.EnumResponse.BadRequest) {
        setFeedback({
          message: t.message.auth.email_not_found,
          severity: Enums.EnumFeedback.Error,
        });
      } else if (status === Enums.EnumResponse.Unauthorized) {
        setFeedback({
          message: props.id
            ? t.message.auth.email_not_found
            : t.message.auth.wrong_password,
          severity: Enums.EnumFeedback.Error,
        });
      } else if (status === Enums.EnumResponse.Forbidden) {
        setFeedback({
          message: t.message.auth.not_authorized,
          severity: Enums.EnumFeedback.Error,
        });
      } else if (status === Enums.EnumResponse.NotFound) {
        setFeedback({
          message: t.message.auth.email_not_found,
          severity: Enums.EnumFeedback.Error,
        });
      }
    } else {
      setFeedback({
        message: t.message.feedback.required_fields,
        severity: Enums.EnumFeedback.Info,
      });
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="qh-form"
      style={props.id ? { marginTop: "3.5rem" } : {}}
    >
      <Custom.TextField
        name="email"
        type="email"
        required={true}
        onChange={onChange}
        label={t.label.email}
      />
      {!props.id && (
        <Custom.TextField
          name="password"
          type="password"
          required={true}
          onChange={onChange}
          label={t.label.password}
        />
      )}
      <div className="qh-control">
        <Custom.Button submit>{t.action.sign_in}</Custom.Button>
      </div>
    </form>
  );
};
